import React, { ReactElement, memo } from 'react';
import { Control, Controller } from 'react-hook-form';
import ServerErrorAlert from 'adapter/in/dashboards/components/ServerErrorAlert';
import { Button, Row, Cell, Grid, FormControl, Paragraph } from '@dexma/ui-components';
import i18n from 'asset/translation';
import gtmIds from 'adapter/in/dashboards/config/gtm';
import { StyledCreateDashboardModal } from 'adapter/in/dashboards/pages/Modals/DashboardNameModal/style';

const gtm = gtmIds.dashboard.createModal;
// TODO Move to a common place to use it on edit as well
export const DASHBOARD_NAME_MAX_LENGTH = 140;

export type DashboardNameFormData = {
    name: string;
};

export type DashboardNameModalProps = {
    error: boolean;
    success: boolean;
    isLoading: boolean;
    showModal: boolean;
    handleCloseModal: () => void;
    control: Control<DashboardNameFormData>;
    handleOnSubmit: () => void;
    defaultValue?: string;
    title: string;
};

const DashboardNameModal = memo(
    (props: DashboardNameModalProps): ReactElement => {
        const { showModal, handleCloseModal, error, success, isLoading, control, handleOnSubmit, defaultValue, title } = props;

        return (
            <StyledCreateDashboardModal width={600} visible={showModal} onCancel={handleCloseModal} title={title}>
                <form
                    data-testid='edit-dashboard-modal'
                    defaultValue={defaultValue || 'New dashboard'}
                    onSubmit={(e: React.SyntheticEvent<HTMLFormElement>) => {
                        e.preventDefault();
                        handleOnSubmit();
                    }}
                >
                    <Grid fluid className='no-padding-grid'>
                        <Row>
                            {error && (
                                <Cell>
                                    <ServerErrorAlert tryAgain={handleOnSubmit} dataId={`${gtm.actions.tryAgain}`} />
                                </Cell>
                            )}
                            <Cell xs={12}>
                                <Paragraph margin='1rem 0 1rem 0'>{i18n.t('dashboards.create.name')} </Paragraph>
                            </Cell>

                            <Cell xs={12}>
                                <Controller
                                    control={control}
                                    rules={{
                                        required: { value: true, message: i18n.t('dashboards.create.noNameProvided') },
                                        maxLength: { value: DASHBOARD_NAME_MAX_LENGTH, message: i18n.t('dashboards.create.nameTooLong') },
                                    }}
                                    defaultValue={defaultValue}
                                    name='name'
                                    render={({ field: { onChange, onBlur, value }, fieldState: { error: fieldError } }) => (
                                        <FormControl
                                            data-testid='input-name'
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            control='Input'
                                            disabled={isLoading || success}
                                            value={value ?? ''}
                                            placeholder={i18n.t('dashboards.create.placeholder')}
                                            message={fieldError?.message}
                                            error={fieldError}
                                            dataId={gtm.inputName}
                                        />
                                    )}
                                />
                            </Cell>
                        </Row>
                        <div className='buttons'>
                            <Button
                                variant='primary'
                                type={!success ? 'submit' : 'button'}
                                text={i18n.t('dashboards.create.saveButton')}
                                data-testid={isLoading ? 'loading' : 'submit'}
                                isLoading={isLoading}
                                iconBefore={success ? 'check' : ''}
                                dataId={gtm.actions.save}
                            />
                            <Button
                                variant='outline'
                                text={i18n.t('dashboards.create.cancelButton')}
                                type='button'
                                data-testid='cancel'
                                onClick={() => {
                                    handleCloseModal();
                                }}
                                dataId={gtm.actions.cancel}
                            />
                        </div>
                    </Grid>
                </form>
            </StyledCreateDashboardModal>
        );
    }
);

export default DashboardNameModal;
